import { invariantResponse } from '@epic-web/invariant'
import { json, type LoaderFunctionArgs } from '@remix-run/node'
import { Link, useLoaderData, type MetaFunction } from '@remix-run/react'
import { brandName } from '#app/components/branding.js'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { momentsAgo } from '#app/utils/dateFormat.js'
import { prisma } from '#app/utils/db.server.ts'
import { getUserImgSrc } from '#app/utils/misc.tsx'

export async function loader({ params }: LoaderFunctionArgs) {
	const owner = await prisma.user.findFirst({
		select: {
			id: true,
			name: true,
			username: true,
			image: { select: { id: true } },
		},
		where: { username: params.username },
	})

	invariantResponse(owner, 'Owner not found', { status: 404 })

	const guides = await prisma.guide.findMany({
		where: { creatorId: owner.id },
		select: {
			id: true,
			title: true,
			slug: true,
			description: true,
			category: true,
			server: true,
			updatedAt: true,
			_count: { select: { reactions: true } },
		},
		orderBy: { updatedAt: 'desc' },
	})

	return json({ owner, guides })
}

export default function UserGuidesRoute() {
	const { owner, guides } = useLoaderData<typeof loader>()
	const ownerDisplayName = owner.name ?? owner.username

	return (
		<div className="container mx-auto mb-24 px-4 pt-8 md:mb-48">
			<div className="flex items-center gap-3">
				<Link to={`/users/${owner.username}`}>
					<img
						src={getUserImgSrc(owner.image?.id)}
						alt={ownerDisplayName}
						className="h-10 w-10 rounded-full object-cover md:h-12 md:w-12"
					/>
				</Link>
				<div>
					<Link to={`/users/${owner.username}`} className="text-sm text-muted-foreground hover:underline">
						&larr; {ownerDisplayName}
					</Link>
					<h1 className="text-xl font-bold md:text-2xl">Guides ({guides.length})</h1>
				</div>
			</div>

			<div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
				{guides.length === 0 && (
					<p className="col-span-full text-sm text-muted-foreground">No guides yet.</p>
				)}
				{guides.map((guide) => (
					<Link
						key={guide.id}
						to={`/guide/${guide.slug}`}
						className="group rounded-xl border bg-muted/30 p-4 transition-colors hover:bg-muted/50"
					>
						<div className="flex items-center justify-between">
							{guide.category && (
								<span className="rounded-full bg-green-600/10 px-2 py-0.5 text-xs text-green-600">{guide.category}</span>
							)}
							{guide._count.reactions > 0 && (
								<span className="text-xs text-muted-foreground">{guide._count.reactions} reactions</span>
							)}
						</div>
						<h3 className="mt-2 font-semibold text-sm group-hover:underline md:text-base truncate">{guide.title}</h3>
						{guide.description && (
							<p className="mt-1 text-xs text-muted-foreground line-clamp-2">{guide.description}</p>
						)}
						<p className="mt-2 text-xs text-muted-foreground">
							{guide.server && <>{guide.server} · </>}
							{momentsAgo(guide.updatedAt.toString())} ago
						</p>
					</Link>
				))}
			</div>
		</div>
	)
}

export const meta: MetaFunction<typeof loader> = ({ data, params }) => {
	const displayName = data?.owner.name ?? params.username
	const count = data?.guides.length ?? 0
	return [
		{ title: `${displayName}'s Guides | ${brandName}` },
		{ name: 'description', content: `${displayName}'s ${count} guide${count !== 1 ? 's' : ''} on ${brandName}` },
	]
}

export function ErrorBoundary() {
	return (
		<GeneralErrorBoundary
			statusHandlers={{
				404: ({ params }) => <p>No user with the username "{params.username}" exists</p>,
			}}
		/>
	)
}
